import { Divider, FormControl, MenuItem, Paper, Select, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { RTFOMetricsDataTable } from "./RTFOMetricsDataTable";
import { RTFOMetricsChart } from "./RTFOMetricsChart";
import { useState } from "react";
import { getMetricName, getMetricParamName } from "./RTFOMetricsPanel";
import { AutoGraph, BarChart, Layers, ScatterPlot, ShowChart } from "@mui/icons-material";
import { useTheme } from "@mui/styles";
import { getSeismicParamName } from "./RTFOSeismicityPanel";
import { RTFOMetricsDailyEfficiencyChartMemo } from "./RTFOMetricsDailyEfficiencyChart";
import { RTFOMetricsDailyFracSummaryChartMemo } from "./RTFOMetricsDailyFracSummaryChart";
import RTFOMapWithWellbore from "./RTFOMapWithWellbore";

export const RTFOMetrics = ({ darkMode, currentRTFO, currentWellName, gridView=true }) => {

  const theme = useTheme();

  const [reportDate, setReportDate] = useState(new Date(currentRTFO?.stageData?.[currentRTFO?.stages?.slice(-1)?.[0]]?.stageEndTimestamp || currentRTFO?.stageData?.[currentRTFO?.stages?.slice(-1)?.[0]]?.stageStartTimestamp || new Date()));
  const timezoneOffset = new Date(reportDate)?.getTimezoneOffset() * 60000;
  const units = currentRTFO?.settings?.units || 'metric';

  return (
    <Paper elevation={0} sx={{boxShadow: 1}} style={{margin: '10px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center', backgroundColor: (theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')])}}>
      
      <Typography variant="h6" style={{margin: '20px 20px 0px 20px'}}>
        Metrics Summary
      </Typography>

      <MetricsChartContainer
        darkMode={darkMode}
        currentRTFO={currentRTFO} 
        initialMetric={'stageTime'}
        showWellSelect={true}
        averageMetrics={currentRTFO?.averageMetrics}
        currentWellName={currentWellName}
        units={units}
      />
      <Divider style={{margin: '10px 20px'}} />

      <MetricsChartContainer 
        darkMode={darkMode}
        currentRTFO={currentRTFO} 
        initialMetric={'tonsPerMin'} 
        showWellSelect={false}
        averageMetrics={currentRTFO?.averageMetrics}
        currentWellName={currentWellName}
        units={units}
      />
      <Divider style={{margin: '10px 20px'}} />

      <div style={{margin: '20px 20px 0px 20px', width: 'calc(100% - 40px)', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
        <Typography variant="h6" >
          Daily Metrics Report
        </Typography>
        
        <div style={{display: 'flex', alignItems: 'center'}}>
          <Typography variant="caption" style={{marginRight: 10}}>Report Date:</Typography>
          <TextField
            label="Report Date"
            type="date"
            // defaultValue={() => formatDate(new Date())}
            value={formatDate(reportDate, timezoneOffset)}
            onChange={e => setReportDate(revertDate(e.target.value, timezoneOffset))}
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
          />

        </div>
      </div>

      <div style={{display: 'flex', flexDirection: gridView ? 'row' : 'column', width: '100%', margin: '10px'}}>
        <div style={{width: gridView ? '49%' : '100%', height: gridView ? 540 : 900, marginBottom: gridView ? 0 : 20}}>
          <RTFOMetricsDailyEfficiencyChartMemo gridView={gridView} reportDate={new Date(reportDate)} units={units} />
        </div>
        <div style={{width: gridView ? '49%' : '100%', height: gridView ? 540 : 900}}>
          <RTFOMetricsDailyFracSummaryChartMemo gridView={gridView} reportDate={new Date(reportDate)} units={units} />
        </div>
      </div>
      <Divider style={{margin: '10px 20px'}} />

      <div>
        <RTFOMapWithWellbore isMetricsMap={true} reportDate={reportDate} />
      </div>


      <div style={{width: '100%', height: 700, padding: 20}}>
        <RTFOMetricsDataTable currentRTFO={currentRTFO} darkMode={darkMode} />
      </div>

    </Paper>
  );
};

function MetricsChartContainer({ darkMode, initialMetric, averageMetrics, currentWellName, units}) {

  const [metric, setMetric] = useState(getMetricName(initialMetric));
  const [type, setType] = useState('Bars');
  const [showTrendline, setShowTrendline] = useState(true);
  const [showHistoric, setShowHistoric] = useState(true);
  
  const allMetrics = {
    'Stage Information': [
      'Stage Time',
      '# Clusters',
      'Cluster Spacing',
      'Stage Length'
    ],
    'Pumping Information': [
      'Time to Pre Sand',
      'Propp. Placement Eff.',
      'Fluid Intensity',
      'Sand Per Fluid',
      'Fluid Per Sand',
      'DFR Per Sand',
      'DFR Per Fluid',
      'LFR Per Sand',
      'LFR Per Fluid',
      'Total DFR',
      'Total LFR',
      'Clean Vol. to Pre Sand',
      'Total Slurry Volume',
      'Total Clean Volume', 
      'Total Produced Volume', 
      'Overflush Vol.',
      'Proppant Intensity Planned',
      'Proppant Intensity Actual',
      'Total Proppant',
      'Max Proppant',
      'Average Pumping Pr.',
      'Average Pumping Rate',
      'Well Swap Time'
    ],
    'ISIP': [
      'Breakdown Pressure',
      'ISIP Surf.',
      'ISIP BH',
      'Frac Gradient'
    ],
    'Stepdown Model': [
      'Pre Sand Pressure',
      'Pre Sand Rate',
      'Pre Sand Pnwb',
      'Pre Sand Pperf',
      'Flush Pperf',
      'Pre Sand Open Perfs',
      'Flush Open Perfs',
      'Perf Efficiency',
      'Rate Drop Events',
      'Plug Slip Events',
      'Sandoff Events',
      'Isolation Issue Events',
      'Equipment Issue Events',
      'Lost Suction Events',
      'Other Events',
      'Fracfeed Events'
    ],
    'Frac Model': [
      'A*sqrt(k)',
      'Frac Area',
      'Half Length',
      'Percent Energy Lost',
      'dP Per Log Cycle'
    ],
    'FDI': [
      'Xf',
      'VFR',
    ],
    'Pumpdown': [
      'Pumpdown ISIP',
      'Post Perf ISIP',
      'Delta ISIP'
    ],
    'Seismicity': [
      'Total Daily Events (All)',
      'Total Daily Events (2+ ML)',
      'Daily Freq. (All)',
      'Daily Freq. (2+ ML)'
    ],
    'Daily Pumping Metrics': [
      'Daily Pumping Hours',
      'Daily Clean Volume',
      'Daily Total Length',
      'Daily Clean Vol. per Length'
    ]
  };

  const isDailyMetric = (allMetrics['Seismicity']?.includes(metric) || allMetrics['Daily Pumping Metrics']?.includes(metric));

  return (
    <div style={{width: '100%', height: 540, padding: '50px 20px 20px 20px', position: 'relative'}}>
      <div style={{display: 'flex', position: 'absolute', top: 15, left: 20}}>
        <ToggleButtonGroup
          value={type}
          exclusive
          onChange={(e, newType) => newType && setType(newType)}
          size="small"
          style={{height: 24}}
        >
          <ToggleButton value="Bars" size="small" title="Bar Chart" color={(darkMode ? "" : "primary")}>
            <BarChart style={{fontSize: '16px'}} />
          </ToggleButton>
          <ToggleButton value="Lines" size="small" title="Line Chart" color={(darkMode ? "" : "primary")}>
            <ShowChart style={{fontSize: '16px'}} />
          </ToggleButton>
          <ToggleButton value="Scatter" size="small" title="Scatter Chart" color={(darkMode ? "" : "primary")}>
            <ScatterPlot style={{fontSize: '16px'}} />
          </ToggleButton>
        </ToggleButtonGroup>
        <Divider flexItem orientation="vertical" style={{ margin: 5 }} />
        <ToggleButtonGroup
          value={showTrendline}
          exclusive
          onChange={(e, newShowTrendline) => setShowTrendline(newShowTrendline)}
          size="small"
          style={{height: 24}}
        >
          <ToggleButton value={true} size="small" title="Show Trendline" color={(darkMode ? "" : "primary")}>
            <AutoGraph style={{fontSize: '16px'}} />
          </ToggleButton>
        </ToggleButtonGroup>
        <Divider flexItem orientation="vertical" style={{ margin: 5 }} />
        <ToggleButtonGroup
          value={showHistoric}
          exclusive
          onChange={(e, newShowHistoric) => setShowHistoric(newShowHistoric)}
          size="small"
          style={{height: 24}}
        >
          <ToggleButton value={true} size="small" title="Show Previous Pads" color={(darkMode ? "" : "primary")}>
            <Layers style={{fontSize: '16px'}} />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <FormControl style={{position: 'absolute', top: 10, right: 20}}>
        <Select 
          value={metric}
          label="Metric"
          onChange={(e) => setMetric(e.target.value)}
          size="small"
          variant="standard"
          style={{width: 200, height: 28, zIndex: 69, fontSize: '12px'}}
        >
          <Typography variant="caption" style={{marginLeft: 10}}>Stage Information</Typography>
          {allMetrics['Stage Information']?.map(m => (
            <MenuItem value={m} key={m}>{m}</MenuItem>
          ))}
          <Divider />
          <Typography variant="caption" style={{marginLeft: 10}}>Pumping Information</Typography>
          {allMetrics['Pumping Information']?.map(m => (
            <MenuItem value={m} key={m}>{m}</MenuItem>
          ))}
          <Divider />

          <Typography variant="caption" style={{marginLeft: 10}}>ISIP</Typography>
          {allMetrics['ISIP']?.map(m => (
            <MenuItem value={m} key={m}>{m}</MenuItem>
          ))}
          <Divider />

          <Typography variant="caption" style={{marginLeft: 10}}>Stepdown Model</Typography>
          {allMetrics['Stepdown Model']?.map(m => (
            <MenuItem value={m} key={m}>{m}</MenuItem>
          ))}
          <Divider />

          <Typography variant="caption" style={{marginLeft: 10}}>Frac Model</Typography>
          {allMetrics['Frac Model']?.map(m => (
            <MenuItem value={m} key={m}>{m}</MenuItem>
          ))}
          <Divider />

          <Typography variant="caption" style={{marginLeft: 10}}>FDI</Typography>
          {allMetrics['FDI']?.map(m => (
            <MenuItem value={m} key={m}>{m}</MenuItem>
          ))}
          <Divider />

          <Typography variant="caption" style={{marginLeft: 10}}>Pumpdown</Typography>
          {allMetrics['Pumpdown']?.map(m => (
            <MenuItem value={m} key={m}>{m}</MenuItem>
          ))}
          <Divider />

          <Typography variant="caption" style={{marginLeft: 10}}>Seismicity</Typography>
          {allMetrics['Seismicity']?.map(m => (
            <MenuItem value={m} key={m}>{m}</MenuItem>
          ))}
          <Divider />

          <Typography variant="caption" style={{marginLeft: 10}}>Daily Pumping Metrics</Typography>
          {allMetrics['Daily Pumping Metrics']?.map(m => (
            <MenuItem value={m} key={m}>{m}</MenuItem>
          ))}
          <Divider />
          {/* {metrics?.map((metric) => {
            return (
              <MenuItem value={metric} key={metric}>{metric}</MenuItem>
            );
          })} */}
        </Select>
      </FormControl>

      <RTFOMetricsChart 
        well={currentWellName}
        metric={!isDailyMetric ? getMetricParamName(metric) : getSeismicParamName(metric)} 
        type={type} 
        showTrendline={showTrendline} 
        showHistoric={showHistoric}
        averageMetrics={averageMetrics} 
        isDailyMetric={isDailyMetric}
        units={units}
      />

    </div>  
  );
};

// return date formatted as YYYY-MM-DD
function formatDate(date, timezoneOffset) {
  try {
    if (!date) return new Date()?.toISOString()?.split('T')?.[0];

    const newTime = new Date(new Date(date)?.getTime() - timezoneOffset);

    return newTime?.toISOString()?.split('T')?.[0];
  } catch(error) {
    console.log(error);
    return date;
  };
};

// return date formatted as YYYY-MM-DD
function revertDate(date, timezoneOffset) {
  try {
    if (!date) return new Date()?.toLocaleDateString();

    const newTime = new Date(new Date(date)?.getTime() + timezoneOffset);

    return newTime?.toLocaleDateString();
  } catch(error) {
    console.log(error);
    return date;
  };
};